import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Modal from '../Modal';
import SearchableDropdown from '../SearchableDropdown';
import { useAuth } from '../../context/AuthContext';
import { useInventory } from '../../context/InventoryContext';
import {
  attachGeneratedPurchaseOrderPdf,
  createPurchaseOrder,
  fetchPurchaseOrder,
  lookupPurchaseOrderNumber,
  purchaseOrderApprovalLocked,
  updatePurchaseOrder,
  PurchaseOrderNumberConflictError,
  type PurchaseOrderApproval,
  type PurchaseOrderItemInput,
  type PurchaseOrderItemRow,
  type PurchaseOrderNumberMatch,
  type PurchaseOrderRow,
} from '../../api/purchaseOrders';
import { COMPANY_ADDRESS } from '../../utils/purchaseOrderPdf';
import './PurchaseOrders.css';

/**
 * The purchase order form, laid out like the PO Template sheet: the PO/date/
 * requested-by row, side-by-side vendor and ship-to blocks, the line items, and the
 * totals stack. It both creates a purchase order and edits an existing one — pass
 * `editing` for the latter.
 *
 * Each line is either a pick from the inventory catalog or a write-in. Creating a PO
 * renders the same data to a PDF and posts it back as the PO's first attachment, so
 * the record and the paperwork never drift apart. Editing does not regenerate that PDF
 * on its own: the vendor may already have the original, so the form saves first and
 * then offers to issue an updated one.
 *
 * Line items are only editable while nothing has been received. After that the
 * quantities are tied to stock that has already been booked in, so the lines are shown
 * read-only and the rest of the order stays editable.
 *
 * The PO number is assigned by the server unless the user writes one in — for an order
 * already numbered elsewhere, say by the vendor. A write-in is checked as it is typed
 * and again on submit; if it is taken, the form names the purchase order holding it and
 * offers a link across to it.
 */

const SHIP_TO_DEFAULT = 'TechForce Robotics';

/** Today in the browser's local time, as YYYY-MM-DD for a date input. */
function todayInput(): string {
  const now = new Date();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${m}-${d}`;
}

function money(value: number): string {
  return `$${(Number.isFinite(value) ? value : 0).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

type LineSource = 'inventory' | 'custom';

interface FormLine {
  key: string;
  source: LineSource;
  /** Catalog product name, used by the dropdown; resolved to an id on submit. */
  productName: string;
  /** Free-text description for a write-in. */
  description: string;
  qty: string;
  unitPrice: string;
}

function blankLine(source: LineSource = 'inventory'): FormLine {
  return {
    key: `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`,
    source,
    productName: '',
    description: '',
    qty: '1',
    unitPrice: '',
  };
}

/** The purchase order being edited, or null when creating a new one. */
export interface PurchaseOrderEditTarget {
  purchaseOrder: PurchaseOrderRow;
  items: PurchaseOrderItemRow[];
}

interface PurchaseOrderFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Set to edit that purchase order; leave null to create a new one. */
  editing?: PurchaseOrderEditTarget | null;
  onSaved: (po: PurchaseOrderRow) => void;
  /** Opens an existing PO, used by the link shown when a write-in number is taken. */
  onShowExisting: (purchaseOrderId: number) => void;
}

const PurchaseOrderFormModal: React.FC<PurchaseOrderFormModalProps> = ({
  isOpen,
  onClose,
  editing = null,
  onSaved,
  onShowExisting,
}) => {
  const isEdit = editing != null;
  const selfId = editing?.purchaseOrder.id ?? null;
  // Rewriting lines that already have stock booked against them would strand it.
  const itemsLocked = editing != null && editing.purchaseOrder.qty_received > 0;
  const approvalLocked = editing != null && purchaseOrderApprovalLocked(editing.purchaseOrder);

  const { user } = useAuth();
  const { products } = useInventory();

  const [dateIssued, setDateIssued] = useState(todayInput);
  const [requestedBy, setRequestedBy] = useState(user?.username ?? '');

  const [poNumberMode, setPoNumberMode] = useState<'auto' | 'manual'>('auto');
  const [poNumber, setPoNumber] = useState('');
  const [poNumberConflict, setPoNumberConflict] = useState<PurchaseOrderNumberMatch | null>(null);
  const [poNumberChecking, setPoNumberChecking] = useState(false);

  const [vendorName, setVendorName] = useState('');
  const [vendorAddress, setVendorAddress] = useState('');
  const [vendorContact, setVendorContact] = useState('');
  const [vendorPhoneEmail, setVendorPhoneEmail] = useState('');
  const [paymentTerms, setPaymentTerms] = useState('');

  const [shipToName, setShipToName] = useState(SHIP_TO_DEFAULT);
  const [shipToAddress, setShipToAddress] = useState(COMPANY_ADDRESS);
  const [shipToAttn, setShipToAttn] = useState('');
  const [shipToPhoneEmail, setShipToPhoneEmail] = useState('');
  const [requestedDelivery, setRequestedDelivery] = useState('');
  const [expectedDelivery, setExpectedDelivery] = useState('');
  const [eta, setEta] = useState('');
  const [trackingNumber, setTrackingNumber] = useState('');

  const [lines, setLines] = useState<FormLine[]>([blankLine()]);
  const [shipping, setShipping] = useState('');
  const [processingFee, setProcessingFee] = useState('');
  const [tax, setTax] = useState('');
  const [initialEstimate, setInitialEstimate] = useState('');
  const [notesTerms, setNotesTerms] = useState('');

  const [approvalStatus, setApprovalStatus] = useState<PurchaseOrderApproval>('Pending');
  const [approvedBy, setApprovedBy] = useState('');
  const [approvedDate, setApprovedDate] = useState('');

  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  // After an edit is saved the modal stays open on a short prompt offering to reissue
  // the PDF, holding the freshly saved order so the document matches what was stored.
  const [pdfPrompt, setPdfPrompt] = useState<PurchaseOrderEditTarget | null>(null);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [pdfDone, setPdfDone] = useState(false);

  // Void products cannot be ordered against, so they are left out of the picker.
  const productOptions = useMemo(
    () =>
      products
        .filter((p) => p.catalogStatus !== 'Void')
        .map((p) => p.name)
        .sort((a, b) => a.localeCompare(b)),
    [products]
  );

  const lineTotals = lines.map((l) => (Number(l.qty) || 0) * (Number(l.unitPrice) || 0));
  const subtotal = lineTotals.reduce((sum, n) => sum + n, 0);
  const total = subtotal + (Number(shipping) || 0) + (Number(processingFee) || 0) + (Number(tax) || 0);

  // Check a written-in number while it is being typed, so a clash surfaces before the
  // rest of the form has been filled in. Debounced, and the cancelled flag stops an
  // earlier reply from landing on top of a later one.
  useEffect(() => {
    const candidate = poNumber.trim();
    if (poNumberMode !== 'manual' || !candidate) {
      setPoNumberConflict(null);
      setPoNumberChecking(false);
      return;
    }
    setPoNumberChecking(true);
    let cancelled = false;
    const timer = window.setTimeout(() => {
      lookupPurchaseOrderNumber(candidate)
        .then((match) => {
          // The order being edited already owns its own number; that is not a clash.
          if (!cancelled) setPoNumberConflict(match && match.id === selfId ? null : match);
        })
        .catch(() => {
          // A failed check must not block the form; submit verifies server-side anyway.
          if (!cancelled) setPoNumberConflict(null);
        })
        .finally(() => {
          if (!cancelled) setPoNumberChecking(false);
        });
    }, 400);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [poNumber, poNumberMode, selfId]);

  const resetForm = useCallback(() => {
    setDateIssued(todayInput());
    setRequestedBy(user?.username ?? '');
    setPoNumberMode('auto');
    setPoNumber('');
    setPoNumberConflict(null);
    setPoNumberChecking(false);
    setVendorName('');
    setVendorAddress('');
    setVendorContact('');
    setVendorPhoneEmail('');
    setPaymentTerms('');
    setShipToName(SHIP_TO_DEFAULT);
    setShipToAddress(COMPANY_ADDRESS);
    setShipToAttn('');
    setShipToPhoneEmail('');
    setRequestedDelivery('');
    setExpectedDelivery('');
    setEta('');
    setTrackingNumber('');
    setLines([blankLine()]);
    setShipping('');
    setProcessingFee('');
    setTax('');
    setInitialEstimate('');
    setNotesTerms('');
    setApprovalStatus('Pending');
    setApprovedBy('');
    setApprovedDate('');
    setError('');
    setPdfPrompt(null);
    setPdfBusy(false);
    setPdfDone(false);
  }, [user?.username]);

  const fillFrom = useCallback((target: PurchaseOrderEditTarget) => {
    const { purchaseOrder: po, items } = target;
    setDateIssued(po.date_issued ?? '');
    setRequestedBy(po.requested_by ?? '');
    setPoNumberMode('manual');
    setPoNumber(po.po_number);
    setPoNumberConflict(null);
    setPoNumberChecking(false);
    setVendorName(po.vendor_name);
    setVendorAddress(po.vendor_address ?? '');
    setVendorContact(po.vendor_contact_person ?? '');
    setVendorPhoneEmail(po.vendor_phone_email ?? '');
    setPaymentTerms(po.payment_terms ?? '');
    setShipToName(po.ship_to_name ?? '');
    setShipToAddress(po.ship_to_address ?? '');
    setShipToAttn(po.ship_to_attn ?? '');
    setShipToPhoneEmail(po.ship_to_phone_email ?? '');
    setRequestedDelivery(po.requested_delivery ?? '');
    setExpectedDelivery(po.expected_delivery_date ?? '');
    setEta(po.eta ?? '');
    setTrackingNumber(po.carrier_tracking ?? '');
    setLines(
      items.length > 0
        ? items.map((it) => ({
            key: `item-${it.id}`,
            source: it.is_custom ? 'custom' : 'inventory',
            productName: it.product_name ?? '',
            description: it.description,
            qty: String(it.qty),
            unitPrice: String(it.unit_price),
          }))
        : [blankLine()]
    );
    setShipping(po.shipping ? String(po.shipping) : '');
    setProcessingFee(po.processing_fee ? String(po.processing_fee) : '');
    setTax(po.tax ? String(po.tax) : '');
    setInitialEstimate(po.initial_estimated_cost != null ? String(po.initial_estimated_cost) : '');
    setNotesTerms(po.notes_terms ?? '');
    setApprovalStatus(po.approval_status);
    setApprovedBy(po.approved_by ?? '');
    setApprovedDate(po.approved_date ?? '');
    setError('');
    setPdfPrompt(null);
    setPdfBusy(false);
    setPdfDone(false);
  }, []);

  // Load the form once per opening, so a reopened create form starts clean and an edit
  // form shows the order as it currently stands. Keyed on the target rather than run on
  // every render: a re-render mid-edit must not throw away what has been typed.
  const loadedFor = useRef<string | null>(null);
  useEffect(() => {
    if (!isOpen) {
      loadedFor.current = null;
      return;
    }
    const key = editing ? `edit-${editing.purchaseOrder.id}` : 'create';
    if (loadedFor.current === key) return;
    loadedFor.current = key;
    if (editing) fillFrom(editing);
    else resetForm();
  }, [isOpen, editing, fillFrom, resetForm]);

  const handleClose = () => {
    if (saving || pdfBusy) return;
    resetForm();
    onClose();
  };

  const updateLine = (key: string, patch: Partial<FormLine>) => {
    setLines((prev) => prev.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const writtenIn = poNumberMode === 'manual' ? poNumber.trim().toUpperCase() : '';
    if (poNumberMode === 'manual' && !writtenIn) {
      setError(isEdit ? 'A PO number is required.' : 'Enter a PO number, or switch back to having one assigned.');
      return;
    }
    if (poNumberConflict) {
      setError(`PO number ${poNumberConflict.po_number} is already in use. Choose another.`);
      return;
    }
    if (!vendorName.trim()) {
      setError('Vendor name is required.');
      return;
    }
    if (!expectedDelivery) {
      setError('An expected delivery date is required — it drives the overdue status.');
      return;
    }

    const items: PurchaseOrderItemInput[] = [];
    if (!itemsLocked) {
      for (const line of lines) {
        const isCustom = line.source === 'custom';
        const description = (isCustom ? line.description : line.productName).trim();
        if (!description) continue;
        const qty = Number(line.qty) || 0;
        if (qty <= 0) {
          setError(`Enter a quantity greater than zero for "${description}".`);
          return;
        }
        const product = isCustom ? undefined : products.find((p) => p.name === line.productName);
        if (!isCustom && !product) {
          setError(`"${line.productName}" is not an inventory product. Pick one from the list or switch the line to a custom item.`);
          return;
        }
        items.push({
          product_id: product ? product.id : null,
          is_custom: isCustom,
          description,
          qty,
          unit_price: Number(line.unitPrice) || 0,
        });
      }
      if (items.length === 0) {
        setError('Add at least one item to the purchase order.');
        return;
      }
    }

    const estimate = initialEstimate.trim() === '' ? null : Number(initialEstimate);
    if (estimate != null && (!Number.isFinite(estimate) || estimate < 0)) {
      setError('Enter the initial estimated cost as an amount of zero or more, or leave it blank.');
      return;
    }

    const fields = {
      date_issued: dateIssued || null,
      requested_by: requestedBy.trim() || null,
      approval_status: approvalStatus,
      approved_by: approvedBy.trim() || null,
      approved_date: approvedDate || null,
      vendor_name: vendorName.trim(),
      vendor_address: vendorAddress.trim() || null,
      vendor_contact_person: vendorContact.trim() || null,
      vendor_phone_email: vendorPhoneEmail.trim() || null,
      payment_terms: paymentTerms.trim() || null,
      ship_to_name: shipToName.trim() || null,
      ship_to_address: shipToAddress.trim() || null,
      ship_to_attn: shipToAttn.trim() || null,
      ship_to_phone_email: shipToPhoneEmail.trim() || null,
      requested_delivery: requestedDelivery || null,
      expected_delivery_date: expectedDelivery,
      eta: eta || null,
      carrier_tracking: trackingNumber.trim() || null,
      shipping: Number(shipping) || 0,
      processing_fee: Number(processingFee) || 0,
      tax: Number(tax) || 0,
      initial_estimated_cost: estimate,
      notes_terms: notesTerms.trim() || null,
    };

    setSaving(true);
    try {
      if (editing) {
        const saved = await updatePurchaseOrder(editing.purchaseOrder.id, {
          ...fields,
          po_number: writtenIn,
          ...(itemsLocked ? {} : { items }),
        });
        onSaved(saved);
        // Re-read rather than trust local state, so an offered PDF is built from what
        // was actually stored.
        const fresh = await fetchPurchaseOrder(saved.id);
        setPdfPrompt({ purchaseOrder: fresh.purchaseOrder, items: fresh.items });
        return;
      }

      const { purchaseOrder, items: savedItems } = await createPurchaseOrder({
        user_id: user?.id ?? null,
        po_number: writtenIn || null,
        ...fields,
        items,
      });

      // The PDF is part of the deliverable, but the PO already exists at this point.
      // A rendering or upload failure must not read as "the PO was not created".
      try {
        await attachGeneratedPurchaseOrderPdf(purchaseOrder, savedItems, user?.id ?? null);
      } catch (pdfError) {
        console.warn('[PurchaseOrder] PDF could not be attached:', pdfError);
      }

      resetForm();
      onSaved(purchaseOrder);
    } catch (err) {
      // Someone took the number between the typed check and this submit.
      if (err instanceof PurchaseOrderNumberConflictError) {
        setPoNumberConflict(err.conflict);
        setError(err.message);
      } else {
        setError(
          err instanceof Error
            ? err.message
            : isEdit
              ? 'Failed to save the purchase order.'
              : 'Failed to generate the purchase order.'
        );
      }
    } finally {
      setSaving(false);
    }
  };

  const handleRegeneratePdf = async () => {
    if (!pdfPrompt) return;
    setPdfBusy(true);
    setError('');
    try {
      await attachGeneratedPurchaseOrderPdf(pdfPrompt.purchaseOrder, pdfPrompt.items, user?.id ?? null);
      setPdfDone(true);
      onSaved(pdfPrompt.purchaseOrder);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate the PDF.');
    } finally {
      setPdfBusy(false);
    }
  };

  // Saved, and now offering to reissue the document rather than doing it unasked: the
  // vendor may already be holding the original PDF.
  if (pdfPrompt) {
    return (
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        title={`${pdfPrompt.purchaseOrder.po_number} saved`}
        narrow
        elevated
      >
        <div className="modal-body">
          {pdfDone ? (
            <p className="po-success">
              An updated PDF is attached to {pdfPrompt.purchaseOrder.po_number}. The previous one was voided and is
              still on the record.
            </p>
          ) : (
            <p className="po-detail-prose">
              Your changes to <strong>{pdfPrompt.purchaseOrder.po_number}</strong> are saved. The attached PDF still
              shows the old details — generate a new one to replace it? The current PDF is voided rather than deleted,
              so whatever the vendor already has stays on the record.
            </p>
          )}
          {error ? <p className="create-order-error" role="alert">{error}</p> : null}
        </div>
        <div className="modal-actions">
          <button type="button" className="cancel-button" onClick={handleClose} disabled={pdfBusy}>
            {pdfDone ? 'Close' : 'Not now'}
          </button>
          {!pdfDone && (
            <button type="button" className="po-primary-btn" disabled={pdfBusy} onClick={() => void handleRegeneratePdf()}>
              {pdfBusy ? 'Generating…' : 'Generate updated PDF'}
            </button>
          )}
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={isEdit ? `Edit ${editing.purchaseOrder.po_number}` : 'Generate Purchase Order'}
      extraWide
      // Editing opens over the detail modal, so it has to stack above it.
      elevated={isEdit}
    >
      <form onSubmit={handleSubmit}>
        <div className="modal-body">
          <div className="po-form-letterhead">
            <strong>TECHFORCE ROBOTICS</strong>
            <span>{COMPANY_ADDRESS}</span>
          </div>

          <div className="po-form-grid po-form-grid--3">
            <div className="form-group">
              <label className="form-label" htmlFor="po-number">PO Number</label>
              {/* An existing order already has a number, so editing it is just a text
                  field — there is nothing to assign. */}
              {!isEdit && (
                <div className="po-number-modes" role="radiogroup" aria-label="How to set the PO number">
                  <label className="po-number-mode">
                    <input
                      type="radio"
                      name="po-number-mode"
                      value="auto"
                      checked={poNumberMode === 'auto'}
                      onChange={() => {
                        setPoNumberMode('auto');
                        setError('');
                      }}
                    />
                    <span>Assign for me</span>
                  </label>
                  <label className="po-number-mode">
                    <input
                      type="radio"
                      name="po-number-mode"
                      value="manual"
                      checked={poNumberMode === 'manual'}
                      onChange={() => {
                        setPoNumberMode('manual');
                        setError('');
                      }}
                    />
                    <span>Write it in</span>
                  </label>
                </div>
              )}
              {poNumberMode === 'auto' ? (
                <input type="text" className="form-input" value="Assigned when generated" disabled />
              ) : (
                <>
                  <input
                    id="po-number"
                    type="text"
                    className={`form-input${poNumberConflict ? ' po-input-error' : ''}`}
                    value={poNumber}
                    onChange={(e) => {
                      setPoNumber(e.target.value);
                      setError('');
                    }}
                    placeholder="e.g. PO-2026-014"
                    autoComplete="off"
                    aria-invalid={poNumberConflict != null}
                  />
                  {poNumberChecking && <p className="po-form-hint">Checking…</p>}
                  {poNumberConflict && (
                    <p className="po-number-conflict" role="alert">
                      {poNumberConflict.po_number} is already in use
                      {poNumberConflict.vendor_name ? ` by ${poNumberConflict.vendor_name}` : ''}.{' '}
                      <button
                        type="button"
                        className="po-link-btn"
                        onClick={() => onShowExisting(poNumberConflict.id)}
                      >
                        View it
                      </button>
                    </p>
                  )}
                </>
              )}
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="po-date-issued">Date Issued</label>
              <input
                id="po-date-issued"
                type="date"
                className="form-input"
                value={dateIssued}
                onChange={(e) => setDateIssued(e.target.value)}
              />
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="po-requested-by">Requested By</label>
              <input
                id="po-requested-by"
                type="text"
                className="form-input"
                value={requestedBy}
                onChange={(e) => setRequestedBy(e.target.value)}
              />
            </div>
          </div>

          <div className="po-form-blocks">
            <section className="po-form-block">
              <h4 className="po-form-block-title">Vendor</h4>
              <div className="form-group">
                <label className="form-label" htmlFor="po-vendor-name">Vendor Name *</label>
                <input
                  id="po-vendor-name"
                  type="text"
                  className="form-input"
                  value={vendorName}
                  onChange={(e) => setVendorName(e.target.value)}
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="po-vendor-address">Address</label>
                <input
                  id="po-vendor-address"
                  type="text"
                  className="form-input"
                  value={vendorAddress}
                  onChange={(e) => setVendorAddress(e.target.value)}
                />
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="po-vendor-contact">Contact Person</label>
                <input
                  id="po-vendor-contact"
                  type="text"
                  className="form-input"
                  value={vendorContact}
                  onChange={(e) => setVendorContact(e.target.value)}
                />
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="po-vendor-phone">Phone / Email</label>
                <input
                  id="po-vendor-phone"
                  type="text"
                  className="form-input"
                  value={vendorPhoneEmail}
                  onChange={(e) => setVendorPhoneEmail(e.target.value)}
                />
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="po-payment-terms">Payment Terms</label>
                <input
                  id="po-payment-terms"
                  type="text"
                  className="form-input"
                  value={paymentTerms}
                  onChange={(e) => setPaymentTerms(e.target.value)}
                  placeholder="e.g. Credit card upon delivery"
                />
              </div>
            </section>

            <section className="po-form-block">
              <h4 className="po-form-block-title">Ship To</h4>
              <div className="form-group">
                <label className="form-label" htmlFor="po-ship-name">Ship To</label>
                <input
                  id="po-ship-name"
                  type="text"
                  className="form-input"
                  value={shipToName}
                  onChange={(e) => setShipToName(e.target.value)}
                />
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="po-ship-address">Address</label>
                <input
                  id="po-ship-address"
                  type="text"
                  className="form-input"
                  value={shipToAddress}
                  onChange={(e) => setShipToAddress(e.target.value)}
                />
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="po-ship-attn">Attn</label>
                <input
                  id="po-ship-attn"
                  type="text"
                  className="form-input"
                  value={shipToAttn}
                  onChange={(e) => setShipToAttn(e.target.value)}
                />
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="po-ship-phone">Phone / Email</label>
                <input
                  id="po-ship-phone"
                  type="text"
                  className="form-input"
                  value={shipToPhoneEmail}
                  onChange={(e) => setShipToPhoneEmail(e.target.value)}
                />
              </div>
              <div className="po-form-grid po-form-grid--2">
                <div className="form-group">
                  <label className="form-label" htmlFor="po-requested-delivery">Requested Delivery</label>
                  <input
                    id="po-requested-delivery"
                    type="date"
                    className="form-input"
                    value={requestedDelivery}
                    onChange={(e) => setRequestedDelivery(e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label" htmlFor="po-expected-delivery">Expected Delivery *</label>
                  <input
                    id="po-expected-delivery"
                    type="date"
                    className="form-input"
                    value={expectedDelivery}
                    onChange={(e) => setExpectedDelivery(e.target.value)}
                    required
                  />
                  <span className="po-form-hint">A PO past this date is flagged Overdue.</span>
                </div>
              </div>
              <div className="po-form-grid po-form-grid--2">
                <div className="form-group">
                  <label className="form-label" htmlFor="po-eta">ETA</label>
                  <input
                    id="po-eta"
                    type="date"
                    className="form-input"
                    value={eta}
                    onChange={(e) => setEta(e.target.value)}
                  />
                  <span className="po-form-hint">The latest arrival estimate from the vendor or carrier.</span>
                </div>
                <div className="form-group">
                  <label className="form-label" htmlFor="po-tracking-number">Tracking Number</label>
                  <input
                    id="po-tracking-number"
                    type="text"
                    className="form-input"
                    value={trackingNumber}
                    onChange={(e) => setTrackingNumber(e.target.value)}
                    placeholder="Carrier and tracking #"
                  />
                </div>
              </div>
            </section>
          </div>

          <h4 className="po-form-block-title po-form-items-title">Items</h4>
          {itemsLocked ? (
            <>
              <p className="po-form-hint">
                Part of this order has been received, so the lines are fixed — changing them would strand the
                quantities already booked in. Everything else above and below can still be edited.
              </p>
              <div className="po-table-wrapper">
                <table className="po-table">
                  <thead>
                    <tr>
                      <th className="po-col-num">#</th>
                      <th>Description</th>
                      <th className="po-col-num">Qty</th>
                      <th className="po-col-num">Received</th>
                      <th className="po-col-num">Unit Price</th>
                      <th className="po-col-num">Line Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(editing?.items ?? []).map((item) => (
                      <tr key={item.id}>
                        <td className="po-col-num">{item.line_no}</td>
                        <td>
                          {item.description}
                          {item.is_custom ? <span className="po-custom-tag">Custom</span> : null}
                        </td>
                        <td className="po-col-num">{item.qty}</td>
                        <td className="po-col-num">{item.qty_received}</td>
                        <td className="po-col-num">{money(item.unit_price)}</td>
                        <td className="po-col-num">{money(item.line_total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <div className="po-form-items">
              {lines.map((line, idx) => (
                <div className="po-form-item" key={line.key}>
                  <div className="po-form-item-head">
                    <span className="po-form-item-no">{idx + 1}</span>
                    <div className="po-form-item-source" role="group" aria-label={`Item ${idx + 1} source`}>
                      <label>
                        <input
                          type="radio"
                          name={`po-source-${line.key}`}
                          checked={line.source === 'inventory'}
                          onChange={() => updateLine(line.key, { source: 'inventory', description: '' })}
                        />
                        Inventory product
                      </label>
                      <label>
                        <input
                          type="radio"
                          name={`po-source-${line.key}`}
                          checked={line.source === 'custom'}
                          onChange={() => updateLine(line.key, { source: 'custom', productName: '' })}
                        />
                        Custom item
                      </label>
                    </div>
                    {lines.length > 1 && (
                      <button
                        type="button"
                        className="po-form-item-remove"
                        onClick={() => setLines((prev) => prev.filter((l) => l.key !== line.key))}
                        aria-label={`Remove item ${idx + 1}`}
                      >
                        Remove
                      </button>
                    )}
                  </div>

                  <div className="po-form-item-body">
                    <div className="form-group po-form-item-desc">
                      <label className="form-label">Description</label>
                      {line.source === 'inventory' ? (
                        <SearchableDropdown
                          options={productOptions}
                          value={line.productName}
                          onChange={(value) => updateLine(line.key, { productName: value })}
                          placeholder="Search inventory products..."
                          noResultsMessage="No product matches — switch to Custom item to write it in"
                        />
                      ) : (
                        <input
                          type="text"
                          className="form-input"
                          value={line.description}
                          onChange={(e) => updateLine(line.key, { description: e.target.value })}
                          placeholder="e.g. 2 sample bins and lids with central hole"
                        />
                      )}
                    </div>
                    <div className="form-group po-form-item-qty">
                      <label className="form-label">Qty</label>
                      <input
                        type="number"
                        min="0"
                        step="any"
                        className="form-input"
                        value={line.qty}
                        onChange={(e) => updateLine(line.key, { qty: e.target.value })}
                      />
                    </div>
                    <div className="form-group po-form-item-price">
                      <label className="form-label">Unit Price</label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        className="form-input"
                        value={line.unitPrice}
                        onChange={(e) => updateLine(line.key, { unitPrice: e.target.value })}
                        placeholder="0.00"
                      />
                    </div>
                    <div className="form-group po-form-item-total">
                      <label className="form-label">Line Total</label>
                      <div className="po-form-readout">{money(lineTotals[idx])}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {!itemsLocked && (
            <div className="po-form-items-actions">
              <button type="button" className="po-secondary-btn" onClick={() => setLines((prev) => [...prev, blankLine()])}>
                + Add item
              </button>
              <button
                type="button"
                className="po-secondary-btn"
                onClick={() => setLines((prev) => [...prev, blankLine('custom')])}
              >
                + Add custom item
              </button>
            </div>
          )}

          <div className="po-form-totals">
            <div className="po-form-totals-inputs">
              <div className="form-group">
                <label className="form-label" htmlFor="po-shipping">Shipping / Freight</label>
                <input
                  id="po-shipping"
                  type="number"
                  min="0"
                  step="0.01"
                  className="form-input"
                  value={shipping}
                  onChange={(e) => setShipping(e.target.value)}
                  placeholder="0.00"
                />
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="po-processing-fee">Processing Fee</label>
                <input
                  id="po-processing-fee"
                  type="number"
                  min="0"
                  step="0.01"
                  className="form-input"
                  value={processingFee}
                  onChange={(e) => setProcessingFee(e.target.value)}
                  placeholder="0.00"
                />
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="po-tax">Tax</label>
                <input
                  id="po-tax"
                  type="number"
                  min="0"
                  step="0.01"
                  className="form-input"
                  value={tax}
                  onChange={(e) => setTax(e.target.value)}
                  placeholder="0.00"
                />
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="po-initial-estimate">Initial Estimated Cost</label>
                <input
                  id="po-initial-estimate"
                  type="number"
                  min="0"
                  step="0.01"
                  className="form-input"
                  value={initialEstimate}
                  onChange={(e) => setInitialEstimate(e.target.value)}
                  placeholder="0.00"
                />
                <span className="po-form-hint">Kept for comparison; not added to the total.</span>
              </div>
            </div>
            <dl className="po-form-totals-readout">
              <div><dt>Subtotal</dt><dd>{money(subtotal)}</dd></div>
              <div><dt>Shipping</dt><dd>{money(Number(shipping) || 0)}</dd></div>
              <div><dt>Processing Fee</dt><dd>{money(Number(processingFee) || 0)}</dd></div>
              <div><dt>Tax</dt><dd>{money(Number(tax) || 0)}</dd></div>
              <div className="po-form-totals-grand"><dt>Total</dt><dd>{money(total)}</dd></div>
              {initialEstimate.trim() !== '' && (
                <div className="po-form-totals-estimate">
                  <dt>Initial estimated cost</dt>
                  <dd>{money(Number(initialEstimate) || 0)}</dd>
                </div>
              )}
            </dl>
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="po-notes-terms">Notes / Terms</label>
            <textarea
              id="po-notes-terms"
              className="form-textarea"
              rows={3}
              value={notesTerms}
              onChange={(e) => setNotesTerms(e.target.value)}
            />
          </div>

          <div className="po-form-grid po-form-grid--3">
            <div className="form-group">
              <label className="form-label" htmlFor="po-approval-status">Approval Status</label>
              <select
                id="po-approval-status"
                className="form-input"
                value={approvalStatus}
                disabled={approvalLocked}
                onChange={(e) => setApprovalStatus(e.target.value as PurchaseOrderApproval)}
              >
                <option value="Pending">Pending</option>
                <option value="Approved">Approved</option>
                <option value="Rejected">Rejected</option>
              </select>
              <span className="po-form-hint">
                {approvalLocked
                  ? 'Items on this order have been received, so it stays approved.'
                  : 'Only an approved purchase order can be received.'}
              </span>
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="po-approved-by">Approved By</label>
              <input
                id="po-approved-by"
                type="text"
                className="form-input"
                value={approvedBy}
                onChange={(e) => setApprovedBy(e.target.value)}
              />
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="po-approved-date">Approval Date</label>
              <input
                id="po-approved-date"
                type="date"
                className="form-input"
                value={approvedDate}
                onChange={(e) => setApprovedDate(e.target.value)}
              />
            </div>
          </div>

          {error ? <p className="create-order-error" role="alert">{error}</p> : null}
        </div>

        <div className="modal-actions">
          <button type="button" className="cancel-button" onClick={handleClose} disabled={saving}>
            Cancel
          </button>
          <button type="submit" className="po-primary-btn" disabled={saving}>
            {isEdit
              ? saving
                ? 'Saving…'
                : 'Save changes'
              : saving
                ? 'Generating…'
                : 'Generate Purchase Order'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default PurchaseOrderFormModal;
